import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpGraduatedResponsePage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', IconComponent: Icons.ClipboardDocumentList },
    { id: 'what-is-gr', name: 'What is Graduated Response?', IconComponent: Icons.QuestionMarkCircle },
    { id: 'generating-reports', name: 'Generating Reports', IconComponent: Icons.ChartBar },
    { id: 'using-reports', name: 'Using Reports', IconComponent: Icons.Target },
    { id: 'best-practices', name: 'Best Practices', IconComponent: Icons.LightBulb }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.ClipboardDocumentList className="w-8 h-8 mr-3" /> Graduated Response Report Help</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to generating and using graduated response reports for SEN students
              </p>
            </div>
            <Link
              to="/graduated-response-report"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Generate Report
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Graduated Response Report Overview</h2>
                <p className="text-gray-600 mb-4">
                  The Graduated Response Report system helps SENCOs and educational staff document the 
                  support provided to students with special educational needs. It tracks interventions, 
                  progress, and next steps in a systematic way that supports the graduated approach to SEN provision.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.PencilSquare className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Documentation</h3>
                    <p className="text-sm text-gray-600 mt-2">Comprehensive record of support provided</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ChartBar className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Progress Tracking</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor effectiveness of interventions</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ArrowPath className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Next Steps</h3>
                    <p className="text-sm text-gray-600 mt-2">Plan future support and interventions</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'what-is-gr' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Graduated Response?</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Definition</h3>
                    <p className="text-blue-800 mb-3">
                      Graduated Response is a systematic approach to identifying and meeting the special 
                      educational needs of children and young people. It involves a cycle of assessment, 
                      planning, implementation, and review.
                    </p>
                    <p className="text-blue-800">
                      The approach ensures that children receive the right support at the right time, 
                      with interventions becoming more intensive if needed.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2">Key Principles</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Early identification of needs</li>
                        <li>• Appropriate and timely support</li>
                        <li>• Regular review and assessment</li>
                        <li>• Involvement of parents/carers</li>
                        <li>• Collaboration with specialists</li>
                      </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-900 mb-2">The Cycle</h3>
                      <ul className="text-purple-800 space-y-1">
                        <li>1. Assess - Identify needs</li>
                        <li>2. Plan - Design interventions</li>
                        <li>3. Do - Implement support</li>
                        <li>4. Review - Evaluate effectiveness</li>
                        <li>5. Repeat - Adjust and continue</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">Why It's Important</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Ensures systematic approach to SEN provision</li>
                      <li>• Provides evidence for decision-making</li>
                      <li>• Supports communication with parents and professionals</li>
                      <li>• Helps track progress over time</li>
                      <li>• Supports requests for additional funding or support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'generating-reports' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Generating Reports</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step-by-Step Process</h3>
                    <ol className="text-blue-800 space-y-2 ml-4">
                      <li>1. Select Student
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Choose the student for the report</li>
                          <li>• Ensure student profile is complete</li>
                          <li>• Review existing intervention data</li>
                        </ul>
                      </li>
                      <li>2. Choose Report Type
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Initial assessment report</li>
                          <li>• Progress review report</li>
                          <li>• End of intervention report</li>
                          <li>• Annual review report</li>
                        </ul>
                      </li>
                      <li>3. Input Data
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Current areas of need</li>
                          <li>• Interventions implemented</li>
                          <li>• Progress made</li>
                          <li>• Challenges encountered</li>
                        </ul>
                      </li>
                      <li>4. Generate Report
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• System creates comprehensive report</li>
                          <li>• Review and edit if necessary</li>
                          <li>• Save to student records</li>
                        </ul>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Required Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">Student Details</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Current areas of need</li>
                          <li>• Previous interventions</li>
                          <li>• Progress indicators</li>
                          <li>• Parent/carer input</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-green-900 mb-2">Intervention Data</h4>
                        <ul className="text-green-800 text-sm space-y-1">
                          <li>• Types of support provided</li>
                          <li>• Frequency and duration</li>
                          <li>• Staff involved</li>
                          <li>• Resources used</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2">Report Templates</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Standard graduated response format</li>
                      <li>• Customizable sections</li>
                      <li>• Professional appearance</li>
                      <li>• Easy to read and understand</li>
                      <li>• Suitable for sharing with stakeholders</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'using-reports' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Using Reports</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Internal Use</h3>
                      <ul className="text-blue-800 space-y-1">
                        <li>• Planning future interventions</li>
                        <li>• Staff training and development</li>
                        <li>• Resource allocation decisions</li>
                        <li>• Policy development</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Handshake className="w-5 h-5 mr-2" /> External Sharing</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Parents and carers</li>
                        <li>• Other professionals</li>
                        <li>• Local authority officers</li>
                        <li>• Educational psychologists</li>
                      </ul>
                    </div>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Decision Making</h3>
                    <p className="text-gray-600 mb-2">
                      Use reports to inform important decisions about student support.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Whether to continue current interventions</li>
                      <li>• Need for additional support</li>
                      <li>• Referral to external specialists</li>
                      <li>• Request for statutory assessment</li>
                      <li>• Resource allocation</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Monitoring</h3>
                    <p className="text-gray-600 mb-2">
                      Track student progress over time using multiple reports.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Compare progress across terms</li>
                      <li>• Identify patterns and trends</li>
                      <li>• Evaluate intervention effectiveness</li>
                      <li>• Plan for long-term development</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center"><Icons.ArrowUp className="w-5 h-5 mr-2" /> Communication Tool</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Clear documentation for meetings</li>
                      <li>• Evidence for funding requests</li>
                      <li>• Support for professional discussions</li>
                      <li>• Historical record for transitions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'best-practices' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Best Practices</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.PencilSquare className="w-5 h-5 mr-2" /> Report Writing</h3>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Use clear, objective language</li>
                      <li>• Include specific examples and evidence</li>
                      <li>• Focus on student progress and needs</li>
                      <li>• Avoid jargon and technical terms</li>
                      <li>• Be concise but comprehensive</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Calendar className="w-5 h-5 mr-2" /> Regular Updates</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Update reports regularly (termly or more often)</li>
                      <li>• Include recent progress and developments</li>
                      <li>• Reflect current needs and circumstances</li>
                      <li>• Maintain continuity with previous reports</li>
                      <li>• Plan for next review period</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.Handshake className="w-5 h-5 mr-2" /> Collaboration</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Involve all relevant staff members</li>
                      <li>• Seek input from parents and carers</li>
                      <li>• Consult with external professionals</li>
                      <li>• Share reports appropriately</li>
                      <li>• Use reports for team discussions</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center"><Icons.Lock className="w-5 h-5 mr-2" /> Data Protection</h3>
                    <ul className="text-orange-800 space-y-1">
                      <li>• Ensure data accuracy and security</li>
                      <li>• Follow GDPR and data protection guidelines</li>
                      <li>• Control access to sensitive information</li>
                      <li>• Maintain appropriate retention periods</li>
                      <li>• Secure storage and transmission</li>
                    </ul>
                  </div>

                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Use templates to ensure consistency</li>
                      <li>• Keep reports factual and evidence-based</li>
                      <li>• Regular training on report writing</li>
                      <li>• Peer review of reports before sharing</li>
                      <li>• Use reports to inform future planning</li>
                      <li>• Maintain a clear audit trail</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpGraduatedResponsePage; 